
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, BlogPost } from '../../lib/supabase';

const keywords = [
  'OBS Studio', 'ATEM Mini', 'マイク選び', 'エンコーダー',
  '音声トラブル', '映像トラブル', 'ネットワーク', 'YouTube Live',
  'Zoom配信', 'ハイブリッド配信', 'スイッチャー', 'カメラ設定',
  '照明', 'グリーンバック', 'NDI', 'RTMP',
];

const Sidebar = () => {
  const navigate = useNavigate();
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<{ name: string; count: number }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('is_published', true)
        .order('published_date', { ascending: false })
        .limit(50);

      if (error) throw error;
      const posts = data || [];

      // カテゴリー集計
      const catMap: Record<string, number> = {};
      posts.forEach((p) => {
        catMap[p.category] = (catMap[p.category] || 0) + 1;
      });
      setCategories(Object.entries(catMap).map(([name, count]) => ({ name, count })));

      // 注目記事（最新8件）
      setFeaturedPosts(posts.slice(0, 8));
    } catch (err) {
      console.error('サイドバーデータ取得エラー:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <aside className="w-72 space-y-8 flex-shrink-0">
      {/* カテゴリー */}
      <div>
        <h3 className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-4">
          <span className="w-5 h-5 flex items-center justify-center text-gray-700">
            <i className="ri-edit-2-line text-base"></i>
          </span>
          カテゴリー
        </h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-5 bg-gray-100 rounded animate-pulse"></div>
            ))
          ) : (
            categories.map((cat) => (
              <a
                key={cat.name}
                href={`/blog?category=${encodeURIComponent(cat.name)}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/blog?category=${encodeURIComponent(cat.name)}`);
                }}
                className="text-sm text-gray-700 hover:text-cyan-600 transition-colors cursor-pointer truncate"
              >
                {cat.name}
              </a>
            ))
          )}
        </div>
      </div>

      {/* 注目の記事 */}
      <div>
        <h3 className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-4">
          <span className="w-5 h-5 flex items-center justify-center text-gray-700">
            <i className="ri-file-text-line text-base"></i>
          </span>
          注目の記事
        </h3>
        <div className="space-y-3">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex gap-3 animate-pulse">
                <div className="w-20 h-14 bg-gray-100 rounded flex-shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gray-100 rounded w-16"></div>
                  <div className="h-3 bg-gray-100 rounded"></div>
                </div>
              </div>
            ))
          ) : (
            featuredPosts.map((post) => (
              <a
                key={post.id}
                href={`/blog/${post.slug}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/blog/${post.slug}`);
                }}
                className="flex gap-3 group cursor-pointer"
              >
                <div className="w-20 h-14 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[11px] text-gray-500">{post.category}</span>
                  <h4 className="text-xs font-semibold text-gray-800 leading-snug line-clamp-2 group-hover:text-cyan-600 transition-colors mt-0.5">
                    {post.title}
                  </h4>
                </div>
              </a>
            ))
          )}
        </div>
      </div>

      {/* キーワード */}
      <div>
        <h3 className="flex items-center gap-2 text-sm font-bold text-gray-900 mb-4">
          <span className="w-5 h-5 flex items-center justify-center text-gray-700">
            <i className="ri-price-tag-3-line text-base"></i>
          </span>
          キーワード
        </h3>
        <div className="flex flex-wrap gap-2">
          {keywords.map((kw) => (
            <a
              key={kw}
              href={`/blog?search=${encodeURIComponent(kw)}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/blog?search=${encodeURIComponent(kw)}`);
              }}
              className="px-3 py-1.5 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 transition-colors cursor-pointer"
            >
              {kw}
            </a>
          ))}
        </div>
      </div>

      {/* お問い合わせCTA */}
      <div className="bg-gray-900 rounded-lg p-5 text-white">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-6 h-6 flex items-center justify-center">
            <i className="ri-customer-service-2-line text-lg"></i>
          </span>
          <h3 className="text-sm font-bold">ご相談・お問い合わせ</h3>
        </div>
        <p className="text-xs text-gray-300 mb-4 leading-relaxed">
          配信に関するお悩みやご質問がございましたら、お気軽にお問い合わせください。
        </p>
        <a
          href="/contact"
          onClick={(e) => {
            e.preventDefault();
            navigate('/contact');
          }}
          className="block w-full bg-white text-gray-900 font-bold py-2.5 px-4 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-sm text-center whitespace-nowrap"
        >
          お問い合わせはこちら
        </a>
      </div>
    </aside>
  );
};

export default Sidebar;
